import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import useLiterals from 'utils/hooks/useLiterals';
import { getMercadoPagoInstance } from 'utils/mercadopago';
import {
  mountField,
  cleanupMainFields,
  whenDomReady,
  waitForContainer,
} from 'utils/mercadopagoFields';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import Switch from 'components/UI/Switch';
import Debugger from 'utils/debugger';
import { captureMercadoPagoError } from 'utils/sendSentryLog';
import { paymentTypeIdToCardType } from 'utils/mercadopago';

/**
 * Formulario de pago usando Core Methods de Mercado Pago.
 * Usa literals.mercadopago.form para textos.
 * Documentación: https://www.mercadopago.com.mx/developers/es/docs/checkout-api-orders/payment-integration/cards
 */
const PaymentForm = ({
  amount = 0,
  onPaymentReady = () => {},
  onCancel = () => {},
}) => {
  const literals = useLiterals('mercadopago');
  const formLiterals = literals.form || {};
  const placeholders = formLiterals.placeholders || {};
  const { user } = useSelector((state) => state.session);
  const [cardholderName, setCardholderName] = useState('');
  const [issuer, setIssuer] = useState('');
  const [issuerThumbnail, setIssuerThumbnail] = useState('');
  const [issuerName, setIssuerName] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentTypeId, setPaymentTypeId] = useState('credit_card');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  const cardNumberFieldRef = useRef(null);
  const expirationDateFieldRef = useRef(null);
  const securityCodeFieldRef = useRef(null);
  const mpInstanceRef = useRef(null);
  const currentBinRef = useRef(null);
  const cardStageDataRef = useRef(null);

  const updateInstallments = useCallback(async (paymentMethod, bin, currentAmount) => {
    try {
      if (!mpInstanceRef.current) return;
      const amountToUse = currentAmount || amount;
      const installmentsData = await mpInstanceRef.current.getInstallments({
        amount: String(amountToUse),
        bin,
        paymentTypeId: 'credit_card',
      });

      if (installmentsData && installmentsData[0]) {
        const installmentData = installmentsData[0];

        setPaymentTypeId(installmentData.payment_type_id || 'credit_card');

        // Obtener el issuer del response
        if (installmentData.issuer) {
          const issuerId = installmentData.issuer.id;
          const issuerThumb = installmentData.issuer.thumbnail || installmentData.issuer.secure_thumbnail;
          const issuerNameValue = installmentData.issuer.name;

          setIssuer(issuerId);
          setIssuerThumbnail(issuerThumb);
          setIssuerName(issuerNameValue);
        }
      }
    } catch (err) {
      Debugger.error('error getting installments:', err);
    }
  }, [amount]);

  // Inicializar campos de Mercado Pago
  useEffect(() => {
    let cancelled = false;

    const initFields = async () => {
      try {
        // Borrar campos previos al entrar/re-entrar (atrás y al frente)
        await cleanupMainFields();
        cardNumberFieldRef.current = null;
        expirationDateFieldRef.current = null;
        securityCodeFieldRef.current = null;

        const mp = await getMercadoPagoInstance();
        if (cancelled) return;
        mpInstanceRef.current = mp;

        whenDomReady(() => {
          (async () => {
            try {
              await waitForContainer('form-checkout__cardNumber');
              if (cancelled) return;

              const cardField = await mountField('cardNumber', 'form-checkout__cardNumber', {
                placeholder: placeholders.cardNumber || 'Número de la tarjeta',
              });
              if (cancelled) return;
              if (cardField && cardField.field) {
                cardNumberFieldRef.current = cardField.field;

                cardNumberFieldRef.current.on('binChange', async (data) => {
                  const { bin } = data;

                  setTimeout(async () => {
                    try {
                      if (!bin && paymentMethodId) {
                        setPaymentMethodId('');
                        setPaymentTypeId('credit_card');
                        setIssuer('');
                        setIssuerThumbnail('');
                        setIssuerName('');
                        currentBinRef.current = null;
                        return;
                      }

                      if (bin && bin.length >= 6 && bin !== currentBinRef.current) {
                        currentBinRef.current = bin;

                        try {
                          const { results } = await mp.getPaymentMethods({ bin });

                          if (results && results.length > 0) {
                            const paymentMethod = results[0];
                            const newPaymentMethodId = paymentMethod.id;

                            setPaymentMethodId(newPaymentMethodId);

                            updateInstallments(paymentMethod, bin, amount).catch((err) => {
                              Debugger.error('error updating installments:', err);
                            });
                          }
                        } catch (methodError) {
                          Debugger.error('error getting payment methods:', methodError);
                        }
                      }
                    } catch (err) {
                      Debugger.error('error in binChange handler:', err);
                    }
                  }, 200);
                });
              }

              const expField = await mountField('expirationDate', 'form-checkout__expirationDate', {
                placeholder: placeholders.expiration || 'MM/YY',
              });
              if (expField && expField.field) {
                expirationDateFieldRef.current = expField.field;
              }

              const secField = await mountField('securityCode', 'form-checkout__securityCode', {
                placeholder: placeholders.securityCode || 'Código de seguridad',
              });
              if (secField && secField.field) {
                securityCodeFieldRef.current = secField.field;
              }
            } catch (initErr) {
              Debugger.error('Error al inicializar campos de Mercado Pago:', initErr);
              captureMercadoPagoError(initErr, {
                feature: 'PaymentForm',
                step: 'initFields',
              });
            }
          })();
        });
      } catch (initError) {
        Debugger.error('Error al inicializar campos de Mercado Pago:', initError);
        captureMercadoPagoError(initError, {
          feature: 'PaymentForm',
          step: 'initFields',
        });
      }
    };

    initFields();

    return () => {
      cancelled = true;
      cardNumberFieldRef.current = null;
      expirationDateFieldRef.current = null;
      securityCodeFieldRef.current = null;
      cleanupMainFields().catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const errs = formLiterals.errors || {};
    const newErrors = {};

    if (!cardholderName || cardholderName.trim().length < 3) {
      newErrors.cardholderName = errs.invalidName || 'Nombre del titular inválido';
    }

    if (!paymentMethodId) {
      newErrors.paymentMethod = errs.invalidPaymentMethod || 'Por favor, ingresa un número de tarjeta válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!mpInstanceRef.current) {
      setError(formLiterals.errors?.notReady || 'Mercado Pago no está listo. Por favor, intenta de nuevo.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await mpInstanceRef.current.fields.createCardToken({
        cardholderName,
        identificationType: 'DNI',
        identificationNumber: '',
      });

      if (!token || !token.id) {
        throw new Error(formLiterals.errors?.tokenFailed || 'No se pudo generar el token.');
      }

      onPaymentReady({
        token: token.id || '',
        paymentMethodId,
        cardholderName,
        issuer: issuer || '',
        installments: '1', // Las mensualidades siempre son 1
        amount,
        cardNumber: token.trunc_card_number.replace(/X/g, '*') || '',
        cardType: paymentTypeIdToCardType(paymentTypeId),
        saveCard,
      });

      setLoading(false);
    } catch (tokenError) {
      captureMercadoPagoError(tokenError, { feature: 'PaymentForm', step: 'tokenization' });
      setError(formLiterals.errors?.processFailed || 'Error al procesar la tarjeta');
      setLoading(false);
    }
  };

  return (
    <>
      <form id='form-checkout' onSubmit={handleSubmit}>
        <div className='payment-form'>
          <h4 className='mb-4'>{formLiterals.title}</h4>

          <div className='mb-4'>
            <label className='form-label'>{formLiterals.cardNumber}</label>
            <div id='form-checkout__cardNumber' className='mp-field-container' />
          </div>

          <div className='grid grid-cols-2 gap-x-6 mb-4'>
            <div>
              <label className='form-label'>{formLiterals.expirationDate}</label>
              <div id='form-checkout__expirationDate' className='mp-field-container' />
            </div>
            <div>
              <label className='form-label'>{formLiterals.securityCode}</label>
              <div id='form-checkout__securityCode' className='mp-field-container' />
            </div>
          </div>

          <Input
            label={formLiterals.cardholder}
            value={cardholderName}
            onChange={setCardholderName}
            placeholder={formLiterals.cardholderPlaceholder}
            error={errors.cardholderName}
            className='mb-4'
          />

          <div className='mb-4'>
            <Switch
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
              label={formLiterals.saveCard}
            />
          </div>

          {issuerThumbnail && (
            <div className='mb-4'>
              <label className='form-label'>{formLiterals.issuer}</label>
              <div className='payment-form-issuer-box'>
                <img
                  src={issuerThumbnail}
                  alt={issuerName || formLiterals.issuer}
                  className='payment-form-issuer-img'
                />
                <span className='payment-form-issuer-name'>{issuerName || formLiterals.issuer}</span>
              </div>
            </div>
          )}

          {errors.paymentMethod && (
            <div className='alert alert-danger mb-4'>{errors.paymentMethod}</div>
          )}

          <div className='flex gap-2'>
            {onCancel && (
              <Button
                text={formLiterals.cancel || literals.common?.cancel}
                type='secondary'
                className='flex-1'
                onClick={onCancel}
                disabled={loading}
              />
            )}
            <Button
              text={formLiterals.continue || literals.common?.continue}
              type='primary'
              className={onCancel ? 'flex-1' : 'w-full'}
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            />
          </div>
        </div>
      </form>

      {error && (
        <ErrorModal
          title={literals.errors?.title || 'Error'}
          description={error}
          onClose={() => setError(null)}
        />
      )}
    </>
  );
};

PaymentForm.propTypes = {
  amount: PropTypes.number.isRequired,
  onPaymentReady: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default PaymentForm;
